import { useEffect, useState } from 'react';
import { Alert, Button, Center, Container, Divider, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { Navigate, useLocation } from 'react-router-dom';
import { isSessionLocallyValid } from '../api/auth';
import { ApiError } from '../api/http';
import PageShell from '../components/PageShell';
import RequestLinkForm from '../components/RequestLinkForm';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { saveReturnTo } from '../hooks/returnTo';
import type { SSOConfig } from '../types/schema';
import { getAuthConfigurationError, hasUsableSSOConfig } from '../utils/authConfig';
import { getPortalPathInfo } from '../utils/portalPath';
import { generateCodeChallenge, generateCodeVerifier, generateState, savePKCEState } from '../utils/pkce';
import NotFoundPage from './NotFoundPage';

export default function AuthRequestPage() {
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? null;
  const [ssoLoading, setSsoLoading] = useState(false);

  // Persist returnTo for AuthVerifyPage, which opens from email in a new tab.
  useEffect(() => {
    if (returnTo) saveReturnTo(returnTo);
  }, [returnTo]);

  const { data: config, isLoading, error, sessionScopeKey } = useResolvedProgram();

  useEffect(() => {
    if (config?.pages?.login?.title) {
      document.title = config.pages.login.title;
    }
  }, [config?.pages?.login?.title]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error instanceof ApiError && error.status === 404) {
    return <NotFoundPage />;
  }

  if (error || !config) {
    return (
      <Container size="sm" pt="xl">
        <Alert color="red" title="Configuration Error">
          Unable to load form configuration. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  if (sessionScopeKey && isSessionLocallyValid(sessionScopeKey)) {
    return <Navigate to={returnTo ?? '/'} replace />;
  }

  const hasMagicLinks = config.magicLinksEnabled === true;
  const hasUsableSSO = hasUsableSSOConfig(config.sso);
  const ssoConfig = hasUsableSSO ? config.sso : null;
  const authConfigurationError = getAuthConfigurationError(config);

  async function handleSSOLogin(sso: SSOConfig) {
    setSsoLoading(true);
    try {
      const state = generateState();
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      savePKCEState(state, codeVerifier);

      if (returnTo) saveReturnTo(returnTo);

      const { basename } = getPortalPathInfo();
      const redirectUri = `${window.location.origin}${basename}/auth/sso/callback`;

      const authUrl = new URL(sso.authorizeUrl);
      authUrl.searchParams.set('client_id', sso.clientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', sso.scopes.join(' '));
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');

      window.location.href = authUrl.toString();
    } catch {
      setSsoLoading(false);
    }
  }

  return (
    <PageShell
      config={config}
      headerLogoUrl={config.theme.uiStyle === 'lovable' ? '' : config.pages?.login?.logoUrl ?? config.theme.logoUrl}
      headerTitle={config.pages?.login?.title ?? config.theme.displayName}
      headerSubtitle="Secure access is required."
      support={config.pages?.login?.showSupportInfo === true ? config.support : undefined}
    >
      {authConfigurationError ? (
        <Paper p="lg" withBorder radius="md" className="portal-panel">
          <Alert color="red" title="Configuration Error">
            {authConfigurationError}
          </Alert>
        </Paper>
      ) : null}
      {ssoConfig && (
          <Paper p="lg" withBorder radius="md" className="portal-panel portal-panel-muted">
            <Stack gap="md">
              <Text size="sm" c="dimmed">Sign in with your organization&apos;s account.</Text>
              <Button
                fullWidth
                loading={ssoLoading}
                onClick={() => { void handleSSOLogin(ssoConfig); }}
                className="portal-primary-button"
              >
                Sign in with {ssoConfig.provider === 'okta' ? 'Okta' : 'SSO'}
              </Button>
            </Stack>
          </Paper>
        )}
        {ssoConfig && hasMagicLinks && <Divider label="or" labelPosition="center" />}
        {hasMagicLinks && (
        <Paper p="lg" withBorder radius="md" className="portal-panel">
          <Title order={4} mb="md" className="portal-section-title">
            Request a secure sign-in link
          </Title>
          <RequestLinkForm description="Enter your authorized work email address and we'll send you a one-time sign-in link." returnTo={returnTo ?? undefined} />
        </Paper>
        )}
    </PageShell>
  );
}